import { NgClass, NgTemplateOutlet } from "@angular/common";
import {
  Component,
  Output,
  EventEmitter,
  inject,
  signal,
  OnInit,
  OnDestroy,
  input,
  WritableSignal,
  ChangeDetectionStrategy,
} from "@angular/core";
import { SvgIconComponent } from "../../components/svg-icon/svg-icon.component";
import { SelectButtonModule } from "@openng/optimus-ui/selectbutton";
import { FormsModule } from "@angular/forms";
import { first, Subscription } from "rxjs";
import { TranslateModule } from "@ngx-translate/core";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { TooltipModule } from "@openng/optimus-ui/tooltip";
import { ASSET_TYPES, AssetType } from "../../../enums/assetTypes.enum";
import { PermissionService } from "../../../services/api/permission/permission.service";
import { HasPermissionDirective } from "../../../shared/directives/has-permission/has-permission.directive";
import { TimeoutComponent } from "../../components/timeout/timeout.component";
import { createTimeoutSignal } from "../../utils/timeout.utils";
import { SimplebarAngularModule } from "simplebar-angular";
import { AssetHierarchyDisplay } from "../../../models/ui/assetHierarchyDisplay";
import { AssetHierarchyDTO } from "../../../models/api/asset/assetHierarchyDTO";
import { DropdownOption } from "../../../models/ui/dropdownOption";
import { SEVERITY } from "../../../constants/severities";
import { AssetHierarchyService } from "../../../services/api/asset-hierarchy/asset-hierarchy.service";
import { RealtimeSeverity } from "../../../models/ws/realtimeSeverity";
import { SeveritySubscriptionHandle, SeverityService } from "../../../services/websocket/severity/severity.service";

@Component({
  standalone: true,
  selector: "app-asset-hierarchy",
  imports: [
    NgTemplateOutlet,
    FormsModule,
    NgClass,
    SvgIconComponent,
    SelectButtonModule,
    TranslateModule,
    ProgressSpinnerModule,
    TooltipModule,
    HasPermissionDirective,
    TimeoutComponent,
    SimplebarAngularModule,
  ],
  templateUrl: "./asset-hierarchy.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./asset-hierarchy.component.scss",
})
export class AssetHierarchyComponent implements OnInit, OnDestroy {
  public permissions = inject(PermissionService);
  public iconsSize = "sm";

  public itemsSelectable = input<boolean>(true);
  public preventTenantClick = input<boolean>(false);
  public statusMode = input<boolean>(false);
  public maxAssetLevel = input<number>(4);
  public initialSelectedAssetId = input<string | null>(null);
  public initialSelectedAssetType = input<string | null>();
  public displayedItemsOptions: DropdownOption[] = [
    {
      value: "all",
      label: "hierarchy_widget.status_mode_options.all",
    },
    {
      value: "fault",
      label: "hierarchy_widget.status_mode_options.fault",
    },
  ];
  public displayedItems = "all";
  @Output() assetClicked = new EventEmitter<AssetHierarchyDisplay>();
  public assetHierarchy: WritableSignal<AssetHierarchyDisplay[]> = signal([]);
  private assetHierarchyService = inject(AssetHierarchyService);
  private severityWebsocket = inject(SeverityService);
  private severitySubscription?: SeveritySubscriptionHandle;

  public loading = signal(false);
  public timeOut = createTimeoutSignal(this.loading);
  public allCollapsed = signal<boolean>(true);
  private shouldRefreshSub!: Subscription;

  public ngOnInit() {
    this.shouldRefreshSub = this.assetHierarchyService.shouldRefreshAssetHierarchy.subscribe({
      next: (shouldRefresh) => {
        if (shouldRefresh) {
          this.getHierarchy();
        }
      },
    });
    this.getHierarchy();
  }

  public ngOnDestroy() {
    this.severitySubscription?.close();

    if (this.shouldRefreshSub) {
      this.shouldRefreshSub.unsubscribe();
    }
  }

  /**
   * Fetches the asset hierarchy from the backend and sets up the UI.
   * If a selected asset ID is provided (either from the route or as an input),
   * marks the corresponding asset as selected.
   */
  public getHierarchy() {
    this.loading.set(true);
    this.assetHierarchyService
      .getAssetHierarchy(ASSET_TYPES.CUSTOMER, this.maxAssetLevel())
      .pipe(first())
      .subscribe((hierarchy: AssetHierarchyDTO[] | null) => {
        if (hierarchy) {
          const rootAssetType = hierarchy[0].assetType as AssetType;

          if (this.statusMode()) {
            // WIP: This is adjusted to FE temporary solution from service
            // It assumes that if TENANT is root asset type its children are already fetched from API.
            // If BE changes asset Hierarchy to accept TENANT as root asset and we use max Level 1 here, then it wont work.
            let customerIds: string[] = [];
            if (rootAssetType === ASSET_TYPES.TENANT && hierarchy[0].children) {
              customerIds = hierarchy[0].children.map((c) => c.id);
            } else if (rootAssetType === ASSET_TYPES.CUSTOMER) {
              customerIds = hierarchy.map((c) => c.id);
            }

            this.severitySubscription?.close();
            const subscription = this.severityWebsocket.register({ customerIds });
            this.severitySubscription = subscription;
            subscription.messages$.subscribe({
              next: (incomingData) => {
                this.handleRealtimeSeverityUpdates(incomingData);
              },
              error: () => undefined,
            });
          }

          this.assetHierarchy.set(this.setupHierarchy(hierarchy, rootAssetType));
          if (this.initialSelectedAssetId() && this.initialSelectedAssetType()) {
            this.assetHierarchy.set(
              this.checkIfSelected(
                this.assetHierarchy(),
                this.initialSelectedAssetId()!,
                this.initialSelectedAssetType()!
              )
            );
          }

          this.loading.set(false);
        }
      });
  }

  /**
   * Handles the updates of the asset hierarchy when the real-time severity changes.
   * Updates the severity of the corresponding assets in the hierarchy that match the given asset type and id.
   * @param incomingData The real-time severity object containing the asset type and id to match, and the new severity.
   * @returns The updated asset hierarchy with the new severity values.
   */
  private handleRealtimeSeverityUpdates(incomingData: RealtimeSeverity) {
    this.assetHierarchy.update((assetHierarchy) => {
      const assetMatchCheck = (assetHierarchy: AssetHierarchyDisplay[]): AssetHierarchyDisplay[] => {
        return assetHierarchy.map((asset) => {
          if (asset.assetType === incomingData.assetType && asset.id === incomingData.assetId) {
            return { ...asset, severity: incomingData.severity };
          } else if (asset.children) {
            return { ...asset, children: assetMatchCheck(asset.children) };
          }
          return asset;
        });
      };

      return assetMatchCheck(assetHierarchy);
    });
  }

  /**
   * Takes an AssetHierarchyDTO[] and returns a new AssetHierarchyDisplay[]
   * with some default values set.
   *
   * @param hierarchy The input hierarchy
   * @param rootAssetType The AssetType of the root asset or first level assets
   * @returns The new hierarchy with default values
   */
  private setupHierarchy(hierarchy: AssetHierarchyDTO[], rootAssetType: AssetType): AssetHierarchyDisplay[] {
    const displayedAssets: AssetHierarchyDisplay[] = [];
    hierarchy.forEach((asset) => {
      const displayedAsset: AssetHierarchyDisplay = {
        ...asset,
        expanded: true,
        selected: false,
        severity: asset.severity || SEVERITY.UNKNOWN.status,
      };
      if (asset.children) {
        displayedAsset.children = this.setupHierarchy(asset.children, rootAssetType);
      }
      displayedAssets.push(displayedAsset);
    });
    return displayedAssets;
  }

  /**
   * Takes an AssetHierarchyDisplay[] and marks the first asset in the hierarchy
   * that matches the given selectedId as selected.
   *
   * @param assetHierarchy The hierarchy to search in
   * @param selectedId The id of the asset that should be marked as selected
   * @returns The modified hierarchy
   */
  private checkIfSelected(assetHierarchy: AssetHierarchyDisplay[], selectedId: string, selectedAssetType: string) {
    assetHierarchy.forEach((asset) => {
      if (asset.id.toString() === selectedId.toString() && asset.assetType === selectedAssetType) {
        asset.selected = true;
      }
      if (asset.children && asset.children.length > 0) {
        asset.children = this.checkIfSelected(asset.children, selectedId, selectedAssetType);
      }
    });
    return assetHierarchy;
  }

  /**
   * Toggles the expanded state of the given asset in the hierarchy.
   * Prevents the event from propagating further.
   *
   * @param asset The asset whose expanded state is to be toggled.
   * @param event The event that triggered the toggle action.
   */
  public toggleExpand(asset: AssetHierarchyDisplay, event: Event) {
    event.stopPropagation();
    asset.expanded = !asset.expanded;
  }

  /**
   * Unselects all assets in the given hierarchy except for the one with the
   * given selectedId.
   *
   * @param asset The asset whose children and itself are to be unselected.
   * @param selectedId The id of the asset that should not be unselected.
   */
  private unSelect(asset: AssetHierarchyDisplay, selectedId: string, selectedAssetType: string) {
    if (asset.id !== selectedId || asset.assetType !== selectedAssetType) {
      asset.selected = false;
    }
    if (asset.children && asset.children.length > 0) {
      asset.children.forEach((childAsset) => {
        this.unSelect(childAsset, selectedId, selectedAssetType);
      });
    }
  }

  /**
   * Emits the selected asset to the parent component.
   * Unselects all other assets in the hierarchy.
   * @param clickedAsset The asset that was clicked.
   */
  public onAssetClick(clickedAsset: AssetHierarchyDisplay) {
    if (clickedAsset.assetType === ASSET_TYPES.TENANT && this.preventTenantClick()) {
      return;
    }
    clickedAsset.selected = true;
    clickedAsset.expanded = true;
    this.assetHierarchy().forEach((asset) => {
      this.unSelect(asset, clickedAsset.id, clickedAsset.assetType);
    });
    this.assetClicked.emit({
      id: clickedAsset.id,
      assetType: clickedAsset.assetType,
      children: clickedAsset.children,
      severity: clickedAsset.severity,
      position: clickedAsset.position,
      label: clickedAsset.label,
    });
  }

  /**
   * Toggles the expanded state for all items in the hierarchy
   */
  public toggleAllExpanded() {
    this.allCollapsed.set(!this.allCollapsed());
    this.assetHierarchy().forEach((child) => this.setExpandWithChildren(child, this.allCollapsed()));
  }

  /**
   * Sets the expanded state for the selected asset and all its children
   * @param asset The target asset whose expanded state should be set (including its children)
   * @param expanded The expanded state to be set
   */
  private setExpandWithChildren(asset: AssetHierarchyDisplay, expanded: boolean) {
    asset.expanded = expanded;
    if (asset.children) {
      asset.children.forEach((child) => this.setExpandWithChildren(child, expanded));
    }
  }
}
